import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { publishApi, PUBLICATIONS_CHANGED_EVENT } from "../api/client";
import type { SitePublication } from "../types";
import PublicationCard from "../components/PublicationCard";
import { useToast } from "../lib/toast";
import "./PublishedSitesView.css";

type Filter = "all" | "live" | "inactive";

export default function PublishedSitesView() {
  const toast = useToast();
  // useToast() returns a fresh object each render; keep it out of deps.
  const toastRef = useRef(toast);
  toastRef.current = toast;
  const [publications, setPublications] = useState<SitePublication[] | null>(null);
  const [connected, setConnected] = useState(false);
  const [filter, setFilter] = useState<Filter>("all");

  const load = useCallback(async () => {
    try {
      setPublications(await publishApi.list());
    } catch (e) {
      setPublications([]);
      toastRef.current.error((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    publishApi.connection().then((c) => setConnected(c.connected)).catch(() => setConnected(false));
    const onChanged = () => void load();
    window.addEventListener(PUBLICATIONS_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(PUBLICATIONS_CHANGED_EVENT, onChanged);
  }, [load]);

  const shown = useMemo(
    () => (publications ?? []).filter((p) => filter === "all" || (filter === "live") === (p.status === "live")),
    [publications, filter],
  );

  return (
    <div className="view">
      <div className="view-header">
        <div>
          <h2 className="view-title">🌐 Published Sites</h2>
          <p className="view-subtitle">Canvases you've published to here.now</p>
        </div>
        <label className="row" style={{ gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
          Show
          <select className="select" style={{ width: "auto" }} value={filter} onChange={(e) => setFilter(e.target.value as Filter)}>
            <option value="all">All</option>
            <option value="live">Live</option>
            <option value="inactive">Expired or deleted</option>
          </select>
        </label>
      </div>

      {!connected && (
        <p className="published-sites-hint">
          Anonymous sites expire after 24 hours. <Link to="/plugins">Connect here.now</Link> to publish permanent sites to your Home or workspaces.
        </p>
      )}

      {publications === null ? (
        <p className="published-sites-hint"><span className="spinner" /> Loading…</p>
      ) : shown.length === 0 ? (
        <div className="empty-state">
          <h3>{publications.length ? "Nothing matches this filter" : "No published sites yet"}</h3>
          {!publications.length && <p>Open a canvas in a chat and choose <strong>Publish…</strong> in its preview.</p>}
        </div>
      ) : (
        <div className="published-sites-list">
          {shown.map((p) => (
            <PublicationCard key={p.id} publication={p} connected={connected} showChatLink />
          ))}
        </div>
      )}
    </div>
  );
}
